import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import Report from 'webapp/models-custom/report';

interface Model {
	includeBgImages?: boolean;
	target?: {
		id: string | null;
		allImages: string[];
	} | null;
}

interface Args {
	model: Model; // BoardShare, ReportScheduleSetting
	isPagelessFormat?: boolean;
	includeBgImages?: boolean;
	onChange?: (selected: boolean) => void;
}

export default class BoardsShareScheduleIncludeBgImagesComponent extends Component<Args> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	@tracked showLargeSizeWarning = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		schedule('afterRender', () => {
			const target = this.args.model.target;
			if (target instanceof Report) {
				// for reports, we must load report data to actually find out about all the images first
				void this.mmReportsService.requestReportData(target.id!).then(() => {
					this.updateShowLargeSizeWarning();
				});
			} else {
				this.updateShowLargeSizeWarning();
			}
		});
	}

	get isSelected() {
		return this.args.includeBgImages ?? this.args.model.includeBgImages ?? false;
	}

	get isDisabled() {
		return this.args.isPagelessFormat;
	}

	@action
	changeSelected(selected: boolean) {
		this.args.model.includeBgImages = selected;
		this.args.onChange?.(selected);
	}

	private updateShowLargeSizeWarning() {
		const images = this.args.model.target?.allImages || [];
		void this.mmBoardsService.getCdnImageSizes(images).then((size) => {
			const limit = 5 * 1000 * 1000; // 5 Mb
			this.showLargeSizeWarning = size > limit;
		});
	}
	<template>
		<div class="include-bg-images">
			<FormCheckboxComponent
				@name="includeBgImages"
				@selected={{this.isSelected}}
				@label="Background images in exported document"
				@onChange={{this.changeSelected}}
				@isDisabled={{this.isDisabled}}
			/>
			{{#unless @isPagelessFormat}}
				{{#if this.showLargeSizeWarning}}
					<span class="warning">
						<CommonUiIconComponent @name="AlertFill" @size="sm" />
						Large file size
						<ToolTipComponent
							@text="This file may not be shareable via email. Background images might increase the file size. Make sure they are optimized or remove them to reduce the size."
							@position="top"
							@maxWidth={{230}}
						/>
					</span>
				{{else}}
					<ToolTipWithIconComponent
						@text="Background images may increase the file size. If included, ensure they are optimized to reduce size."
						@position="top"
						@maxWidth={{230}}
					/>
				{{/if}}
			{{/unless}}
		</div>
	</template>
}
